import { Schema } from "prosemirror-model";
import { docNodeSpec, hardBreakNodeSpec, paragraphNodeSpec, textNodeSpec, } from "../extensions/base/schema";
import { blockquoteNodeSpec } from "../extensions/blockquote/schema";
import { listNodesSpec } from "../extensions/list/schema";
import { codeBlockNodeSpec } from "../extensions/code-block/schema";
import { headingNodeSpec } from "../extensions/heading/schema";
import { horizontalRuleNodeSpec } from "../extensions/horizontal-rule/schema";
import { tableNodesSpec } from "../extensions/table/schema";
import { boldMarkSpec } from "../extensions/bold/schema";
import { codeMarkSpec } from "../extensions/code/schema";
import { colorMarkSpec } from "../extensions/color/schema";
import { italicMarkSpec } from "../extensions/italic/schema";
import { highlightMarkSpec } from "../extensions/highlight/schema";
import { strikethroughMarkSpec } from "../extensions/strikethrough/schema";
import { underlineMarkSpec } from "../extensions/underline/schema";
import { linkMarkSpec } from "../extensions/url/schema";
import OrderedMap from "orderedmap";
const mdImageNodeSpec = () => ({
    image: {
        inline: true,
        attrs: {
            file: { default: {} },
            alignment: { default: "center" },
            size: {
                default: {
                    width: 400,
                },
            },
            caption: { default: "" },
            url: { default: "" },
            alt: { default: null },
        },
        group: "inline",
        draggable: true,
        parseDOM: [
            {
                tag: "img[src]",
                getAttrs(dom) {
                    return {
                        url: dom.getAttribute("src"),
                        alt: dom.getAttribute("alt"),
                        file: {},
                    };
                },
            },
        ],
        toDOM(node) {
            return ["img", node.attrs];
        },
    },
});
const getNodeSpecs = () => [
    docNodeSpec(),
    textNodeSpec(),
    hardBreakNodeSpec(),
    paragraphNodeSpec(),
    blockquoteNodeSpec(),
    listNodesSpec(),
    codeBlockNodeSpec(),
    mdImageNodeSpec(),
    headingNodeSpec(),
    horizontalRuleNodeSpec(),
    tableNodesSpec(),
];
const getMarkSpecs = () => [
    boldMarkSpec(),
    codeMarkSpec(),
    colorMarkSpec(),
    italicMarkSpec(),
    highlightMarkSpec(),
    strikethroughMarkSpec(),
    underlineMarkSpec(),
    linkMarkSpec(),
];
export function createMarkdownSchema() {
    const nodes = getNodeSpecs().reduce((finalSpecs, specGroup) => {
        return finalSpecs.append(specGroup);
    }, OrderedMap.from({}));
    const marks = getMarkSpecs().reduce((finalSpecs, specGroup) => {
        return finalSpecs.append(specGroup);
    }, OrderedMap.from({}));
    return new Schema({
        nodes,
        marks,
    });
}
function listIsTight(tokens, i) {
    while (++i < tokens.length)
        if (tokens[i].type !== "list_item_open")
            return tokens[i].hidden;
    return false;
}
export const markdownTokens = {
    blockquote: {
        block: "blockquote",
    },
    paragraph: {
        block: "paragraph",
    },
    list_item: {
        block: "list_item",
    },
    bullet_list: {
        block: "bullet_list",
        getAttrs: function getAttrs(_, tokens, i) {
            return {
                tight: listIsTight(tokens, i),
            };
        },
    },
    ordered_list: {
        block: "ordered_list",
        getAttrs: function getAttrs(tok, tokens, i) {
            return {
                order: +tok.attrGet("start") || 1,
                tight: listIsTight(tokens, i),
            };
        },
    },
    heading: {
        block: "heading",
        getAttrs: function getAttrs(tok) {
            return {
                level: +tok.tag.slice(1),
            };
        },
    },
    code_block: {
        block: "code_block",
        noCloseToken: true,
    },
    fence: {
        block: "code_block",
        getAttrs: function getAttrs(tok) {
            return {
                params: tok.info || "",
            };
        },
        noCloseToken: true,
    },
    hr: {
        node: "horizontal_rule",
    },
    image: {
        node: "image",
        getAttrs: (tok) => {
            var _a, _b;
            return ({
                src: tok.attrGet("src") || "",
                url: tok.attrGet("src") || "",
                title: tok.attrGet("title") || null,
                alt: (((_a = tok.children) === null || _a === void 0 ? void 0 : _a[0]) && ((_b = tok.children) === null || _b === void 0 ? void 0 : _b[0].content)) || null,
            });
        },
    },
    hardbreak: {
        node: "hard_break",
    },
    em: {
        mark: "em",
    },
    strong: {
        mark: "strong",
    },
    link: {
        mark: "link",
        getAttrs: function getAttrs(tok) {
            return {
                href: tok.attrGet("href"),
                title: tok.attrGet("title") || null,
            };
        },
    },
    code_inline: {
        mark: "code",
        noCloseToken: true,
    },
    table: {
        block: "table",
    },
    thead: {
        block: "table_header",
    },
    tr: {
        block: "table_row",
    },
    th: {
        block: "table_header",
    },
    td: {
        block: "table_cell",
    },
};
